import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  forwardRef,
  OnDestroy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  ValidatorFn,
  FormsModule,
} from '@angular/forms';
import { CurrencyService } from '@app/core/services/currency.service';
import { Subject } from 'rxjs';
import { NgTemplateOutlet, NgClass } from '@angular/common';

@Component({
  selector: 'ag1-financial-input',
  templateUrl: './ag1-financial-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Ag1FinancialInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => Ag1FinancialInputComponent),
      multi: true,
    },
  ],
  imports: [NgTemplateOutlet, FormsModule, NgClass],
})
export class Ag1FinancialInputComponent
  implements ControlValueAccessor, Validator, OnInit, OnChanges, OnDestroy
{
  currencyService = inject(CurrencyService);
  private cdr = inject(ChangeDetectorRef);

  private destroy$ = new Subject<void>();

  private _value: string = '0';
  disabled: boolean = false;
  touched: boolean = false;

  @Input() placeholder: string = '0';
  @Input() readonly: boolean = false;
  @Input() currency: string | null = null;

  @Output() valueFormatted = new EventEmitter<string>();
  @Output() blurEvent = new EventEmitter<void>();

  // limits
  @Input() maxDecimalPlaces: number = 10;
  @Input() maxIntegerPlaces: number = 9;

  pattern: string = `^-?\\d{1,${this.maxIntegerPlaces}}(\\.\\d{1,${this.maxDecimalPlaces}})?$`;
  currencySymbol: string = '';

  // Flag to determine if we're using percentage mode
  get isPercentage(): boolean {
    return this.currency === '%';
  }
  private onTouchedCallback = () => {};

  constructor() {}

  ngOnInit(): void {
    this.updateValidators();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.currency) {
      if (this.isPercentage) {
        this.currencySymbol = '%';
      } else {
        this.currencySymbol = this.currencyService.getCurrencySymbol(
          this.currency,
        );
      }
    }

    if (changes.pattern) {
      this.updateValidators();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get value(): string {
    return this._value;
  }

  set value(val: string) {
    const formatted = this.formatInput(val || '0');
    if (this._value !== formatted) {
      this._value = formatted;
      this.onChange(this._value);
      this.valueFormatted.emit(this._value);
      this.cdr.markForCheck();
    }
  }

  onChange = (_value: string) => {};
  onTouched = () => {};

  writeValue(value: any): void {
    // Designed to work with BigDecimals, however, it
    // will work with any value due to this conversion
    // to a string
    const stringValue: string = value?.toString() || '0';
    const formattedValue: string = this.formatInput(stringValue);
    if (this._value !== formattedValue) {
      this._value = formattedValue;
      this.onChange(this._value);
      this.cdr.markForCheck();
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  onInputChange(event: Event | string): void {
    let cursorPosition = 0;
    let inputElement: HTMLInputElement | null = null;

    if (event instanceof Event) {
      inputElement = event.target as HTMLInputElement;
      cursorPosition = inputElement.selectionStart || 0;
      this.value = inputElement.value;
    } else {
      this.value = event;
    }

    // Force synchronization with view
    if (inputElement && inputElement.value !== this._value) {
      inputElement.value = this._value;
      setTimeout(() => {
        const newPosition = Math.min(cursorPosition, this._value.length);
        inputElement.setSelectionRange(newPosition, newPosition);
      });
    }
  }

  handleBlur(): void {
    this.onTouched();
    this.blurEvent.emit();
    this.cdr.markForCheck();
  }

  validate(control: AbstractControl): ReturnType<ValidatorFn> {
    if (!control.value) {
      return null;
    }

    // Format the value first - this is critical
    const formattedValue = this.formatInput(control.value.toString());

    // Update the control value with the formatted value if they differ
    if (control.value.toString() !== formattedValue) {
      // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        control.setValue(formattedValue, { emitEvent: false });
      });
    }

    // Create regex pattern based on current maxIntegerPlaces and maxDecimalPlaces
    const pattern: RegExp = new RegExp(
      `^-?\\d{1,${this.maxIntegerPlaces}}(\\.\\d{1,${this.maxDecimalPlaces}})?$`,
    );

    // Compare with the formatted value, not the raw control value
    return pattern.test(formattedValue) ? null : { pattern: true };
  }

  private formatInput(input: string): string {
    // if for some reason input is not a string, convert it to string
    input = input?.toString() || '0';

    let value: string = this.stripNonNumericCharacters(input);
    value = this.stripLeadingZeros(value);
    value = this.preventMultipleDecimalPoints(value);
    value = this.toggleMinusSign(value, input);
    value = this.integerLimit(value);
    value = this.decimalLimit(value);

    return value;
  }

  private updateValidators(): void {
    this.pattern = `^-?\\d{1,${this.maxIntegerPlaces}}(\\.\\d{1,${this.maxDecimalPlaces}})?$`;
  }

  private stripNonNumericCharacters(value: string): string {
    // Remove all non-numeric characters except for decimal point and minus sign
    return value.replace(/[^0-9.-]/g, '');
  }

  private stripLeadingZeros(value: string): string {
    if (value === '0') {
      return value;
    }

    if (value === '-0') {
      return value;
    }

    if (value === '-0.') {
      return value;
    }

    if (value === '0.') {
      return value;
    }

    if (value === '') {
      return '0';
    }

    // remove leading zeros
    // eg. 000100 => 100
    // eg. 000.100 => 0.100
    // eg. 000. => 0.
    // eg. 000 => 0
    value = value.replace(/^0+/, '0');
    if (value.match(/^0+\d/)) {
      value = value.replace(/^0+/, '');
    }

    // eg. -000100 => -100
    // eg. -000.100 => -0.100
    // eg. -000. => -0.
    // eg. -000 => -0
    value = value.replace(/^-0+/, '-0');
    if (value.match(/^-0+\d/)) {
      value = value.replace(/^-0+/, '-');
    }

    return value;
  }

  private toggleMinusSign(value: string, input: string): string {
    if (value === '-') {
      return '-0';
    }

    // Strip away minus signs
    value = value.replace(/-/g, '');

    // toggle minus sign
    // eg. 100- => -100
    // eg. -100- => 100
    const inputMinusCount: number = (input.match(/-/g) || []).length;
    const isNegative: boolean = inputMinusCount % 2 === 1;
    if (isNegative) {
      return `-${value}`;
    }

    return value;
  }

  private preventMultipleDecimalPoints(value: string): string {
    const decimalCount: number = (value.match(/\./g) || []).length;
    const decimalIndex: number = value.indexOf('.');

    // add leading zero if decimal point is the first character
    if (value.startsWith('.')) {
      value = `0${value}`;
    }

    // remove all but the first decimal point
    // eg. 100.100.100 => 100.100100
    // eg. 0.2.999.8 => 0.29998
    // eg. 123...123 => 123.123
    if (decimalCount > 1) {
      value = value.replace(/\./g, (_match, index) => {
        return index === decimalIndex ? '.' : '';
      });
    }

    return value;
  }

  private integerLimit(value: string): string {
    const hasDecimalPoint: boolean = value.includes('.');
    const hasMinusSign: boolean = value.includes('-');

    // remove minus sign temporarily
    value = value.replace(/-/g, '');

    // Truncate integers to `maxIntegerPlaces`
    // eg. 1234567890123456789 => 123456789
    // eg. 1234567890123456789.999999999999 => 123456789.999999999999
    const [integer, decimal = ''] = value.split('.');
    if (hasDecimalPoint) {
      value = `${integer.slice(0, this.maxIntegerPlaces)}.${decimal}`;
    } else {
      value = integer.slice(0, this.maxIntegerPlaces);
    }

    // add minus sign back
    if (hasMinusSign) {
      value = `-${value}`;
    }

    return value;
  }

  private decimalLimit(value: string): string {
    const hasDecimalPoint: boolean = value.includes('.');

    // Truncate decimals to `maxDecimalPlaces`
    // eg. 10.9999999999999999 => 10.9999999999
    // eg. 0.1234567890123456789 => 0.1234567890
    const [integer, decimal = ''] = value.split('.');

    if (hasDecimalPoint) {
      return `${integer}.${decimal.slice(0, this.maxDecimalPlaces)}`;
    }

    return integer;
  }
}
